import React, { useEffect, useMemo, useState } from 'react';
import { Play, Video, X } from 'lucide-react';
import { toast } from 'sonner';

import EmptyState from '../components/EmptyState';
import LoadingSkeleton from '../components/LoadingSkeleton';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import api, { employeesApi, mediaApi } from '../lib/api';

function formatTimestamp(value) {
  return new Intl.DateTimeFormat(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(value));
}

export default function Recordings() {
  const [employees, setEmployees] = useState([]);
  const [employeeId, setEmployeeId] = useState('all');
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [recordings, setRecordings] = useState([]);
  const [videoUrls, setVideoUrls] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const [overlay, setOverlay] = useState(null);
  const pageSize = 8;

  useEffect(() => {
    employeesApi
      .list({ page: 1, page_size: 200 })
      .then((response) => setEmployees(response.data?.items || []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    setPage(1);
  }, [employeeId, date]);

  useEffect(() => {
    const loadRecordings = async () => {
      if (!employees.length && employeeId === 'all') return;
      setLoading(true);
      setError('');
      try {
        const targets =
          employeeId === 'all'
            ? employees.map((employee) => ({ id: employee.id, name: employee.name }))
            : employees
                .filter((employee) => employee.id === employeeId)
                .map((employee) => ({ id: employee.id, name: employee.name }));

        const results = await Promise.all(
          targets.map(async (employee) => {
            const response = await mediaApi.recordings(employee.id, date);
            return (response.data?.items || []).map((item) => ({
              ...item,
              employee_name: employee.name,
            }));
          })
        );

        setRecordings(
          results.flat().sort((first, second) => new Date(second.ts_start) - new Date(first.ts_start))
        );
      } catch (err) {
        const message = err.response?.data?.detail || 'Failed to load recordings.';
        setError(message);
        toast.error(message);
      } finally {
        setLoading(false);
      }
    };

    loadRecordings();
  }, [employeeId, date, employees]);

  useEffect(() => {
    recordings.forEach((recording) => {
      if (videoUrls[recording.id]) return;
      api
        .get(recording.signed_view_url, { responseType: 'blob' })
        .then((response) => {
          setVideoUrls((current) => ({
            ...current,
            [recording.id]: URL.createObjectURL(response.data),
          }));
        })
        .catch(() => {});
    });
  }, [recordings, videoUrls]);

  const paginatedRecordings = useMemo(
    () => recordings.slice((page - 1) * pageSize, page * pageSize),
    [recordings, page]
  );

  return (
    <div className="min-h-screen space-y-6 bg-slate-900 p-6 text-slate-300" data-testid="recordings-page">
      <div>
        <h1 className="text-2xl font-bold text-white">Recordings</h1>
        <p className="mt-2 text-slate-500">Review available recording segments by employee and date.</p>
      </div>

      <Card className="border-slate-700 bg-slate-800 shadow-lg">
        <CardHeader className="pb-0">
          <CardTitle className="text-white">Recording Review</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4 pt-6">
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            <Select value={employeeId} onValueChange={setEmployeeId}>
              <SelectTrigger className="border-slate-600 bg-slate-700 text-white">
                <SelectValue placeholder="All Employees" />
              </SelectTrigger>
              <SelectContent className="border-slate-700 bg-slate-800 text-slate-200">
                <SelectItem value="all">All Employees</SelectItem>
                {employees.map((employee) => (
                  <SelectItem key={employee.id} value={employee.id}>
                    {employee.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <input
              type="date"
              value={date}
              onChange={(event) => setDate(event.target.value)}
              className="rounded-lg border border-slate-600 bg-slate-700 px-3 py-2 text-white outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {error ? (
            <div className="rounded-lg border border-red-500/20 bg-red-500/10 p-4 text-sm text-red-300">
              {error}
            </div>
          ) : null}

          {loading ? (
            <LoadingSkeleton type="card" />
          ) : paginatedRecordings.length ? (
            <>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
                {paginatedRecordings.map((recording) => (
                  <button
                    key={recording.id}
                    type="button"
                    className="overflow-hidden rounded-lg border border-slate-700 bg-slate-800 text-left shadow-lg transition hover:border-blue-500/40"
                    onClick={() => setOverlay(recording)}
                  >
                    <div className="flex aspect-video items-center justify-center bg-slate-900 text-slate-400">
                      <Play className="h-10 w-10" />
                    </div>
                    <div className="p-3">
                      <p className="text-sm text-white">{recording.employee_name}</p>
                      <p className="mt-1 text-xs text-slate-500">{formatTimestamp(recording.ts_start)}</p>
                    </div>
                  </button>
                ))}
              </div>
              {recordings.length > pageSize ? (
                <div className="flex items-center justify-between">
                  <p className="text-sm text-slate-500">
                    Page {page} of {Math.max(1, Math.ceil(recordings.length / pageSize))}
                  </p>
                  <div className="flex gap-2">
                    <button
                      className="rounded-lg border border-slate-600 bg-slate-900 px-3 py-2 text-sm text-slate-300 disabled:opacity-40"
                      disabled={page <= 1}
                      onClick={() => setPage((current) => current - 1)}
                    >
                      Previous
                    </button>
                    <button
                      className="rounded-lg border border-slate-600 bg-slate-900 px-3 py-2 text-sm text-slate-300 disabled:opacity-40"
                      disabled={page >= Math.ceil(recordings.length / pageSize)}
                      onClick={() => setPage((current) => current + 1)}
                    >
                      Next
                    </button>
                  </div>
                </div>
              ) : null}
            </>
          ) : (
            <EmptyState
              icon={<Video className="h-8 w-8" />}
              title="No recordings"
              description="No recording segments were found for the selected employee and date."
            />
          )}
        </CardContent>
      </Card>

      {overlay ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/85 p-6">
          <button className="absolute right-6 top-6 text-white" onClick={() => setOverlay(null)}>
            <X className="h-8 w-8" />
          </button>
          <div className="w-full max-w-5xl">
            <video className="max-h-[80vh] w-full rounded-lg bg-black" controls src={videoUrls[overlay.id]} />
            <p className="mt-4 text-center text-sm text-slate-300">
              {overlay.employee_name} • {formatTimestamp(overlay.ts_start)}
            </p>
          </div>
        </div>
      ) : null}
    </div>
  );
}
